import { PaginationMeta, SortParams } from '@recruitee/http-types';
import { SupportedTranslations } from '@recruitee/i18n';

export enum SeatKind {
  Recruitment = 'recruitment',
  CoreHR = 'core_hr',
}

export type App = {
  kind: SeatKind;
  guid: string;
  orgGuid: string;
  externalId?: string;
};

export type Seat = {
  access: boolean;
  status: string; // 'active'
  app: App;
  role: Role | null;
  guid: string;
  locale: string;
  appGuid: string;
  memberGuid: string;
  roleExternalId: null;
};

export type Role = {
  abilities: string[];
  admin: boolean;
  appGuid: string;
  externalId: string;
  id: number;
  name: string;
};

export enum OrgStatus {
  Blocked = 'blocked',
  Active = 'active',
  DeletionRequested = 'deletion_requested',
}

export type OrgDeletionStatus = {
  canceledAt: string;
  orgGuid: string;
  processAt: string;
  requester: {
    name: string;
  };
  userGuid: string;
};

export type Org = {
  name: string;
  guid: string;
  language?: string;
  status: OrgStatus;
  deletionStatus?: OrgDeletionStatus;
};

export enum TellentOrgAbilities {
  ManageTellent = 'manage_tellent',
  ManageUsers = 'manage_users',
  ManageBilling = 'manage_billing',
}

export type TellentMember = {
  owner: boolean;
  status: TeamMemberStatus;
  org: Org;
  firstName: string;
  lastName: string;
  email: string;
  seats: Seat[];
  guid: string;
  userGuid: string;
  orgGuid: string;
  abilities: TellentOrgAbilities[];
  invitation: {
    accepted: boolean;
    id: number;
  } | null;

  avatarThumbUrl: string;
  '2fa': boolean;
};

export enum TeamMemberStatus {
  Active = 'active',
  Invited = 'invited',
  NotInvited = 'notInvited',
  Deactivated = 'deactivated',
}

export type TellentMemberRow = {
  id: string;
  owner: boolean;
  status: TeamMemberStatus;
  org: Org;
  firstName: string;
  lastName: string;
  email: string;
  seats: Seat[];
  guid: string;
  userGuid: string | null;
  orgGuid: string;
  abilities: TellentOrgAbilities[];
  invitation: {
    accepted: boolean;
    id: number;
  } | null;
  avatarThumbUrl: string;
  '2fa': boolean;
  recruitment?: string;
  coreHr?: string;
};

export type InviteMemberPayload = {
  members: Array<Pick<TellentMember, 'email' | 'firstName' | 'lastName'>>;
  seats: Array<Pick<Seat, 'appGuid' | 'access' | 'roleExternalId'>>;
  sendInvitation: boolean;
};

export type TellentMembersResponse = {
  data: TellentMember[];
  meta: PaginationMeta;
};

export type TellentMemberResponse = {
  data: TellentMember;
};

export interface TellentCurrentUser {
  email: string;
  firstName: string;
  lastName: string;
  members: TellentMember[];
  guid: string;
  language?: string; // missing in the response
  timezone?: string;
  timeFormat?: string;
  phone?: string;
  locale: SupportedTranslations;
  weekStartDay?: string;
  appLanguages?: AppLanguages;
  avatarThumbUrl: string;
  currentOrgGuid: string;
}

export interface AppLanguages {
  recruitment?: string;
  coreHr?: string;
  javelo?: string;
}

export interface TellentCurrentUserUpdate {
  firstName: string;
  lastName: string;
  email: string;
  code?: string;
  avatar?: File;
  language?: string;
  timezone?: string;
  timeFormat?: string;
  phone?: string;
  weekStartDay?: string;
  recruiteeLanguage?: string;
  kiwiLanguage?: string;
}

export interface TellentUserResponse {
  user: TellentCurrentUser;
}

export type TellentRole = {
  name: string;
  admin: true;
  externalId: string;
  default: boolean;
};

export type TellentPasswordChange = {
  password: string;
  passwordConfirmation: string;
  code: string;
};

export type TellentPasswordChangeResponse = {
  success: boolean;
};

export interface TellentCommonEnvironment {
  tellentApiUrl: string;
  tellentAuthUrl: string;
  tellentAppUrl: string;
}

export interface TellentAdminAppConfig extends TellentCommonEnvironment {
  tellentAuthTokenUrl: string;
  tellentUserUrl: string;
  headers: {};
}

export type TellentAuthTokenResponse = {
  accessToken: string;
  expiresAt: string;
  expiresIn: number;
  orgGuid: string | null;
  userGuid: string;
};

export type TeamMembersSortBy = 'name' | 'status' | 'recruitment';

export interface TeamMembersPaginationParams {
  page?: number;
  limit?: number;
  q?: string;
  statuses?: TeamMembersStatuses[];
  scope: string;
  sortBy?: TeamMembersSortBy;
  sortOrder?: 'asc' | 'desc';
}
export type TeamMemberPaginationWithSortParams = TeamMembersPaginationParams & SortParams;

export interface TeamMembersSortParams extends SortParams {
  sortBy?: TeamMembersSortBy;
}

export type TeamMembersParams = TeamMemberPaginationWithSortParams & {
  sortBy?: TeamMembersSortBy;
  scope: string;
};

export enum TeamMembersStatuses {
  Active = 'active',
  Deactivated = 'deactivated',
}

export type TellentApps = {
  status: string;
  kind: SeatKind;
  guid: string;
  roles: TellentRole[];
};

export type TellentAppsResponse = {
  data: TellentApps[];
};

export type ManageSeatsResponse = {
  data: [
    {
      access: boolean;
      appGuid: string;
      guid: string;
      roleExternalId: string;
      status: string;
    },
  ];
};

export enum TellentBrand {
  Tellent = 'tellent',
  Recruitee = 'recruitee',
  Kiwi = 'kiwi',
  Javelo = 'javelo',
}

export interface MemberDataUpdate {
  firstName?: string;
  lastName?: string;
  avatar?: File;
  email?: string;
  guid: string;
}

export interface SignInConsent {
  createdAt: string;
  expiresAt: string;
  id: number;
  revokedAt: string | null;
  status: SignInConsentStatus;
}

export enum SignInConsentStatus {
  Active = 'active',
  Revoked = 'revoked',
  Expired = 'expired',
}

export interface SignInConsentResponse {
  data: SignInConsent[];
  meta: PaginationMeta;
}

export interface TellentIAppConfig {
  tellentApiUrl: string;
  tellentAppUrl: string;
  tellentAuthTokenUrl: string;
  tellentAuthUrl: string;
  tellentUserUrl: string;
}

export enum UserProfileTab {
  Profile = 'Profile',
  Companies = 'Companies',
  Security = 'Security',
}
